// API Types based on the backend schema

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  birthDate: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
  ssnLast4?: string | null; // Last 4 digits of SSN
  goal?: string;
  goalTargetPeriod?: string;
  enrolled: boolean;
  demo: boolean;
  subscription: boolean;
  maxPlanEnabled: boolean;
  revenueCatId?: string | null;
  hardUpdateToVersion?: string;
  deleted?: boolean;
  skippedVerification: boolean;
  offercode?: string | null;
  offerCode?: string | null; // Alternative casing from API
  membershipNo?: string | null;
  crsUserId?: string | null; // CRS provider user ID
  creditProvider?: string | null; // Credit provider type (e.g., 'crs', 'identityiq')
  subscriptionType?: 'FREE' | 'PRO_ACCESS' | 'FULL_ACCESS';
  cbSource?: string | null;
  referralId?: string | null;
  isTrial?: boolean;
  trialExpiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
  features?: {
    disputes: boolean;
    disputes2: boolean;
    disputes3: boolean;
    tradelines: boolean;
  };
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
  manager?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
}

export interface ManagerLoginResponse {
  success: boolean;
  user: User;
  manager: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  accessToken: string;
  refreshToken: string;
}

export interface RegisterRequest {
  email: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  birthDate: string;
  ssn: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  phoneNumber: string;
  referralId?: string;
  goal?: string;
  goalTargetPeriod?: string;
  smsCode: string;
}

export interface LoginRequest {
  phoneNumber: string;
  code: string;
}

export interface SendSmsRequest {
  phoneNumber: string;
}

// Credit Analytics Types
export interface CreditAnalytics {
  id: string;
  reportDate: string;
  fullReport: {
    personalInformation: PersonalInformation[];
    creditScores: CreditScore[];
    accountInformation: AccountInformation[];
    publicRecords: PublicRecord[];
    paymentHistoryLegend: Record<string, string>;
    creditInquiries: CreditInquiry[];
    summary: {
      totalCreditLimit: number;
      bureauCreditLimits: Record<string, number>;
      accountsInformation: any[];
    };
  };
  creditFactors: CreditFactor[] | {
    factors: CreditFactor[];
  };
  status?: string;
  message?: string;
  retryAfterSeconds?: number;
  mainAlert?: {
    type: string;
    title: string;
    recommendation: string;
    explanation: string;
    marketplaceButtonText?: string;
    marketplaceButtonId?: string;
  };
  isProcessing: boolean;
  needsRefresh?: boolean;
  bureauCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface PersonalInformation {
  bureau: string;
  name: string;
  alsoKnownAs: string[];
  ssn: string;
  dob: string;
  currentAddress: string;
  previousAddresses: string[];
  employmentHistory: any[];
}

export interface CreditScore {
  bureau: string;
  score: string;
  dateReported: string;
}

export interface AccountInformation {
  id: string;
  creditorName: string;
  creditorDisplayName?: string;
  accountNumber: string;
  accountType: string;
  accountTypeDetailed?: string;
  dateOpened: string;
  originalBalance: string;
  currentBalance: string;
  monthlyPayment: string;
  termLength: string;
  accountStatus: string;
  paymentStatus?: string;
  paymentResponsibility?: string;
  bureau: string;
  paymentHistory: PaymentHistoryEntry[];
  negativeAccount: boolean;
  isLate?: boolean;
  creditLimit: string;
  amountPastDue: string;
  highBalance: string;
  openRevolving: boolean;
  comments?: string[];
  address?: Address;
  phone?: string;
}

export interface PublicRecord {
  type: string;
  filingDate: string;
  resolvedDate: string;
  status: string;
  amount: string;
  bureau: string;
  courtName?: string;
  referenceNumber?: string;
}

export interface PaymentHistoryEntry {
  date: string;
  status: string;
}

export interface CreditFactor {
  id: string;
  title: string;
  impactLevel: string;
  rating: string;
  description: string;
  value: string;
  alerts: any[];
  bureauValues: Record<string, number>;
}

export interface CreditInquiry {
  id: string;
  date: string;
  company: string;
  bureau: string;
  address?: Address;
}

export interface CreditAccount {
  creditorName: string;
  accountNumber: string;
  accountType: string;
  dateOpened: string;
  status: string;
  balance: string;
  creditLimit?: string;
  paymentHistory?: string;
  address?: Address;
}

export interface CreditInquiry {
  date: string;
  company: string;
  type?: string;
}

export interface PersonalInfo {
  type: string;
  value: string;
  bureau?: string;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  postal: string;
}

// Dispute Types
export enum Bureau {
  EXPERIAN = 'Experian',
  EQUIFAX = 'Equifax',
  TRANSUNION = 'TransUnion'
}

export enum DisputeStatus {
  NEW = 'New',
  SENT = 'Sent'
}

export enum DisputeType {
  PERSONAL_INFORMATION_DISPUTE = 'Personal information',
  INQUIRY_REMOVAL = 'Inquiry removal',
  ACCOUNT_DISPUTE = 'Account dispute',
  SECURITY_FREEZE = 'Security freeze'
}

export enum PersonalInfoDisputeReason {
  NAME = 'Name',
  ADDRESS = 'Address',
  EMPLOYMENT = 'Employment',
  DOB = 'DOB'
}

export enum InquiryRemovalReason {
  FRAUDULENT = 'Fraudulent',
  IMPACT = 'Impact',
  DUPLICATE = 'Duplicate'
}

export enum AccountDisputeReason {
  THEFT = 'Theft',
  SETTLED = 'Settled',
  INCORRECT_STATUS = 'Incorrect status',
  INCORRECT_BALANCE = 'Incorrect balance',
  LATE_PAYMENTS = 'Late payments',
  LATE_PAYMENTS_GOODWILL = 'Late payments - Goodwill',
  MISSING_ACCOUNT = 'Missing account'
}

export interface DisputeLetter {
  id: string;
  text: string;
  bureau: Bureau;
  receiverName?: string;
  round?: number;
  lobLetterId?: string;
  lobTrackingUrl?: string;
  lobErrorMessage?: string;
}

export interface Dispute {
  id: string;
  title: string;
  type?: string;
  subTypes: string[];
  letters: DisputeLetter[];
  attachmentNames: string[];
  supportingReason?: string;
  mode?: string;
  createdAt: Date;
  rawRequest?: Record<string, any>;
}

export interface UpdateLetterContentResponse {
  success: boolean;
  message: string;
  letter: {
    id: string;
    bureau: string;
    round: number;
    created_at: string;
    deleted: boolean;
  };
}

export interface EditDisputeRequest {
  reason?: string;
  supportingReason?: string;
  attachmentNames?: string[];
  bureauAccountDisputes?: MultiRoundAccountDisputeRequest['bureauAccountDisputes'];
  bureauInquiryDisputes?: MultiRoundInquiryDisputeRequest['bureauInquiryDisputes'];
  bureauLatesDisputes?: MultiRoundLatesDisputeRequest['bureauAccountDisputes'];
  corrections?: MultiRoundPersonalInfoDisputeRequest['corrections'];
}

// Personal Info Dispute
export interface PersonalInfoCorrection {
  reason: PersonalInfoDisputeReason;
  values: string[];
}

export interface BureauPersonalInfoCorrection {
  bureau: Bureau;
  corrections: PersonalInfoCorrection[];
}

export interface PersonalInfoDisputeRequest {
  bureauPersonalInfoCorrections: BureauPersonalInfoCorrection[];
  attachmentNames?: string[];
  supportingReason?: string;
}

// Inquiry Dispute
export interface DisputeInquiry {
  date: string;
  company: string;
}

export interface BureauInquiryRemoval {
  bureau: Bureau;
  inquiries: DisputeInquiry[];
}

export interface InquiryRemovalRequest {
  reason: InquiryRemovalReason;
  bureauInquiryRemovals: BureauInquiryRemoval[];
  attachmentNames?: string[];
  supportingReason?: string;
}

// Account Dispute
export interface DisputeAccount {
  creditorName: string;
  accountNumber: string;
  accountType: string;
  dateOpened: string;
  status: string;
  balance: string;
  correctStatus?: string;
  correctBalance?: string;
  latePaymentDates?: string[];
  missingBureaus?: string[];
  address?: Address;
}

export interface BureauAccountDispute {
  bureau: Bureau;
  accounts: DisputeAccount[];
}

export interface AccountDisputeRequest {
  reason: AccountDisputeReason;
  bureauAccountDisputes: BureauAccountDispute[];
  attachmentNames?: string[];
  supportingReason?: string;
}

// Security Freeze
export interface SecurityFreezeRequest {
  attachmentNames?: string[];
  supportingReason?: string;
}

// Multiround Disputes
export interface MultiRoundAccountDisputeRequest {
  round?: number; // 1-8 (optional - backend will use default)
  reason: string;
  supportingReason?: string;
  attachmentNames?: string[];
  bureauAccountDisputes: Array<{
    bureau: Bureau;
    account: {
      id: string; // Account ID from credit analytics
      creditorName: string;
      accountNumber: string;
      accountType: string;
      dateOpened: string;
      status: string;
      balance: string;
      latePaymentDates?: string[];
      address?: Address;
    };
  }>;
}

export interface MultiRoundInquiryDisputeRequest {
  round?: number; // 1-8 (optional - backend will use default)
  reason: string;
  supportingReason?: string;
  attachmentNames?: string[];
  bureauInquiryDisputes: Array<{
    bureau: Bureau;
    inquiry: {
      id: string; // Inquiry ID from credit analytics
      date: string;
      company: string;
    };
  }>;
}

export interface MultiRoundLatesDisputeRequest {
  round?: number; // 1-8 (optional - backend will use default)
  reason: string;
  supportingReason?: string;
  attachmentNames?: string[];
  bureauAccountDisputes: Array<{
    bureau: Bureau;
    account: {
      id: string; // Account ID from credit analytics
      creditorName: string;
      accountNumber: string;
      accountType: string;
      dateOpened: string;
      status: string;
      balance: string;
      latePaymentDates?: string[];
      address?: Address;
    };
  }>;
}

export type PersonalInfoCategory = 'Name' | 'Address' | 'Employment' | 'DOB';

export interface MultiRoundPersonalInfoDisputeRequest {
  round?: number; // 1-8 (optional - backend will use default)
  corrections: Array<{
    bureau: Bureau;
    category: PersonalInfoCategory;
    value: string;
  }>;
  supportingReason?: string;
  attachmentNames?: string[];
}

// Tradelines
export interface TradeLine {
  inventoryId: string;
  provider: string;
  name: string;
  ageYears: number;
  creditLimit: number;
  price: number;
  maxAvailableSpots: number;
}

export interface TradeLineSelection {
  tradeLine: TradeLine;
  slots: number;
}

export interface TradeLineAdviceRequest {
  desiredAverageAge: number;
  desiredAverageAgeIncrease?: number;
  currentAverageAge?: number;
  desiredExtraLimit: number;
}

export interface TradeLineAdviceResponse {
  success: boolean;
  selectedTradeLines: TradeLineSelection[];
  newAverageAge: number;
  newTotalLimit: number;
  totalCost: number;
  message: string;
}

export interface TradeLineAdviceOption {
  increaseLevel: 'basic' | 'medium' | 'high' | string;
  desiredAverageAge: number;
  desiredExtraLimit: number;
  advice: TradeLineAdviceResponse;
}

export interface TradeLineInventoryItem {
  id: string;
  provider: string;
  card_name: string;
  open_date: string;
  credit_limit: number;
  cost_per_tradeline: number;
  available_slots: number;
  updated_at: string;
}

export interface TradeLineInventoryResponse {
  success: boolean;
  stats: {
    totalTradelines: number;
    availableTradelines: number;
    totalSlots: number;
    providers: string[];
    lastUpdated: number | null;
  };
  inventory: TradeLineInventoryItem[];
  timestamp: string;
}

export interface RequestedTradeline {
  id: string;
  recipientEmail: string;
  subject: string;
  emailContent: string;
  status: 'sent' | 'failed';
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
  tradelineData: {
    request: TradeLineAdviceRequest;
    result: TradeLineAdviceResponse;
    userInfo: {
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber?: string;
    };
  };
}

export interface RequestedTradelinesResponse {
  success: boolean;
  data: RequestedTradeline[];
  total: number;
}

// Admin
export interface AdminRefreshResponse {
  success: boolean;
  message: string;
  usersProcessed?: number;
  userId?: string;
  errors?: string[];
}

export interface SalesStats {
  totalUsers: number;
  deletedUsers: number;
  enrolledUsers: number;
  activeSubscribers: number;
  productBreakdown: {
    FREE?: number;
    FULL_ACCESS_100?: number;
    PRO_ACCESS_20?: number;
  };
  weekDelta: {
    totalUsers: number;
    deletedUsers: number;
    enrolledUsers: number;
    activeSubscribers: number;
  };
  monthDelta: {
    totalUsers: number;
    deletedUsers: number;
    enrolledUsers: number;
    activeSubscribers: number;
  };
}

export interface SalesStatsResponse {
  success: boolean;
  stats: SalesStats;
  timestamp: string;
}

// User List Types
export interface UserListItem {
  id: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  email: string | null;
  ssnLast4: string | null;
  ssn?: string; // Full SSN for CRS registration
  birthDate?: string; // For CRS registration
  streetAddress?: string; // For CRS registration
  city?: string; // For CRS registration
  state?: string; // For CRS registration
  zip?: string; // For CRS registration
  goal?: string; // For CRS registration
  goalTargetPeriod?: string; // For CRS registration
  subscriptionType: 'FREE' | 'PRO_ACCESS' | 'FULL_ACCESS';
  enrolled: boolean;
  membershipNo: string | null;
  crsUserId: string | null; // CRS provider user ID
  provider?: string; // Credit provider: 'identityiq' or 'crs'
  cbSource?: string | null;
  referralId?: string | null;
  isTrial?: boolean;
  trialExpiresAt?: string | null;
  createdAt: string;
  isDeleted: boolean;
}

export interface UserListPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface UserListResponse {
  success: boolean;
  users: UserListItem[];
  pagination: UserListPagination;
}

// Credit History
export interface CreditHistoryChange {
  title: string;
  summary: string;
}

export interface DisputeOutcomeTracking {
  letterId: string;
  outcome: 'success' | 'failed' | 'partial success';
  reason: string;
}

export interface CreditHistoryEntry {
  scoreNew?: number;
  scoreOld?: number;
  reportTime: string;
  changes?: CreditHistoryChange[];
  disputeOutcomes?: DisputeOutcomeTracking[];
}

export interface CreditHistoryResponse {
  success: boolean;
  bureau: Record<string, CreditHistoryEntry[]>;
}

// Report History Types
export interface ReportDateItem {
  reportDate: string;
  bureauCount: number;
  analyticsId: string;
}

export interface ReportDatesResponse {
  success: boolean;
  userId: string;
  reports: ReportDateItem[];
}

// CRS Enrollment Types
export interface EnrollUserToCRSResponse {
  success: boolean;
  message: string;
  crsUserId?: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    crsUserId: string;
    enrolled: boolean;
  };
}

// Verification Types
export interface VerificationAnswer {
  id: string;
  answer: string;
}

export interface VerificationQuestion {
  question: string;
  answers: VerificationAnswer[];
}

export interface GetVerificationQuestionsResponse {
  success: boolean;
  questions: VerificationQuestion[];
  message: string;
}

export interface SubmitVerificationAnswersRequest {
  answers: string[]; // Array of answer IDs
}

export interface SubmitVerificationAnswersResponse {
  success: boolean;
  message: string;
  status: 'verified' | 'additional_question' | 'failed';
  questions?: VerificationQuestion[]; // Present if status === 'additional_question'
}

// CRS Identity Verification Types
export interface CRSIdentityVerificationResponse {
  success: boolean;
  idpass: boolean;
  requiresSMFA?: boolean;
  smfaLink?: string;
  message?: string;
  fieldErrors?: Array<{ field: string; message: string }>;
}

// User Notes Types
export type NoteAuthorType = 'system' | 'admin' | 'manager';

export interface UserNote {
  id: string;
  userId: string;
  authorType: NoteAuthorType;
  authorId: string | null;
  authorName: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserNotesResponse {
  success: boolean;
  data: UserNote[];
  total: number;
}

export interface UserNoteResponse {
  success: boolean;
  data: UserNote;
}

// Document Review Types
export type DocumentReviewStatus = 'uploaded' | 'approved' | 'rejected' | 'reuploaded' | 'expired';

export interface ReviewComment {
  id: string;
  comment: string;
  status: 'open' | 'closed';
  created_at: number;
  closed_at?: number;
  reviewer_id: string;
}

export interface DocumentMetadata {
  id: string;
  userId: string;
  documentName: string;
  fileSize: number;
  mimeType: string;
  version: number;
  expirationDate?: string;
  createdAt: string;
  updatedAt: string;
}

// Referral + Fuel Credits
export interface ReferralPairingEntry {
  id: string;
  phone_masked: string;
  status: 'awaiting_otp' | 'pending' | 'linked';
  referee_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReferralPairingsResponse {
  pairings: ReferralPairingEntry[];
}

export interface ReferralStats {
  count: number;
  pending: number;
  completed: number;
  rewards_earned: number;
  cap_remaining: number;
  fuel_credits_balance: number;
}

export interface FuelCreditEntry {
  id: string;
  amount: number;
  source: 'referral' | 'admin' | 'purchase' | 'debit' | 'action';
  status: 'claimed' | 'unclaimed' | 'awaiting_trial_purchase';
  reference_id: string | null;
  note: string | null;
  claimed_at: string | null;
  created_at: string;
}

export interface SimulateTrialPurchaseResponse {
  success: boolean;
  message: string;
  trialStartedAt: string;
  trialExpiresAt: string;
  referralRewardActivated: boolean;
}

export interface FuelCreditsHistoryResponse {
  total: number;
  limit: number;
  offset: number;
  entries: FuelCreditEntry[];
}

export interface DocumentWithReview extends DocumentMetadata {
  reviewStatus?: DocumentReviewStatus;
  reviewComments?: ReviewComment[];
}
